<template>
  <v-card>
    <v-form ref="form" :lazy-validation="true">
      <v-card-title>FÖRLÄNG GRUNDUTBILDNING</v-card-title>
      <v-card-actions>
        <v-icon class="close" @click="$emit('close')">$sl-close</v-icon>
      </v-card-actions>
      <v-card-text>
        <p class="mb-4">Om den förväntade sluttiden efter förlängning redan har gått ut behöver du kontakta BYNs kansli.</p>
        <h6 class="text-uppercase semi-bold pb-1 mt-5">Ändra längd</h6>
        <v-select
          v-model="selectMonth"
          :items="timesLengths"
          outlined
          item-text="text"
          item-value="id"
          return-object
          label="Grundutbildningens förlängning"
          clearable
          :disabled="isDisabled"
          hide-details
        ></v-select>
        <p class="caption mt-2 ml-2" :style="{ height: '20px' }" v-html="priceTextDetail"></p>

        <MessageBox v-if="isPendingOrder" type="info">
          <p class="caption mt-2 ml-2">Väntar på godkännande av företag</p>
        </MessageBox>

        <p class="mt-5" v-if="endDate">Förväntat slutdatum för grundutbildningstid på {{ calculatedEducationLength }}: {{ endDate | date }}</p>
        <p class="caption">Eventuella uppehåll har inte tagits med i beräkningen.</p>

        <template v-if="get(this, 'educationTimeLength.value', 0) > agreementLength">
          <MessageBox type="alert" class="mt-n2">
            <p>
              Längden du angivit överstiger den avtalsbaserade tiden.
            </p>
          </MessageBox>
        </template>

      </v-card-text>

      <v-card-actions class="justify-end mt-2">
        <v-btn @click="$emit('close')"> <v-icon>$sl-cancel</v-icon>AVBRYT </v-btn>
        <v-btn :disabled="isDisabled" color="secondary" @click="changeEducationTime"> <v-icon>$sl-check</v-icon>Spara </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import API from '@/services/api.js'
import DatePicker from '@/components/common/DatePicker'
import get from 'lodash/get'
import SugarDate from 'sugar/date'
import MessageBox from '@/components/common/MessageBox.vue'
import { UserRules } from '@/components/mixins'

// @group Grundutbildningstid
// PersonEbooksExtendTimeModal component
export default {
  name: 'PersonEbooksExtendTimeModal',

  mixins: [UserRules],

  props: {
    book: {
      type: Object,
      required: true
    },
    extendTime: {
      type: Boolean,
      default: false,
    }
  },

  components: {
    DatePicker,
    MessageBox
  },

  data() {
    return {
      educationTimeLength: 24,
      timesLengths: [],
      initialStartDate: null,
      agreementLength: null,
      maxExtendCount: 2,
      maxExtendRange: 6,
      selectMonth: {},
      updatedLength: null,
      extendEducationData: []
    }
  },
  created() {
    this.init()

    // `registry` and `admin`- role can extend GU max 3 times up to 12 months.
    if(this.isRegistryRole || this.isAdminRole) {
      this.maxExtendCount = 3
      this.maxExtendRange = 12
    }
  },

  computed: {
    ...mapGetters('basic', ['getQualificationLengthItems']),
    ...mapGetters('person', ['selectedPerson']),
    ...mapGetters('basic', ['getProfessionById']),
    ...mapGetters('currentuser', ['currentUser', 'currentUserRole', 'currentUserId']),

    priceTextDetail() {
      const price = get(this, 'selectMonth.price', 0)
      const theoryCourses = get(this, 'book.education.educationDistanceEducationProviderCourses', [])

      //- Don't show any text if no extension has been selected yet
      if ( !Object.keys(this.selectMonth).length ) return

      //- Show extension cost if one or more courses has yet to be approved
      if ( theoryCourses.some(course => !course.approved) && price > 0 ) {
        return `Observera att denna förlängning kommer att innebära en kostnad på <b>${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} kronor</b>.`
      }
      
      //- Default text
      return 'Denna förlängning är gratis.'
    },

    isDisabled() {
      if ( !this.canExtend ) 
        return true

      if (this.isRegistryRole || this.isAdminRole) {
        return this.extendEducationData.length > 3 || this.updatedLength >= 12;
      }

      return this.extendEducationData.length >= 2 || this.updatedLength >= 6;
    },

    endDate() {
      let fromDate = new Date(get(this, 'book.education.startDate', null))
      let toDate = new Date(get(this,'book.education.calculatedEndDate', null))

      if(this.updatedLength) {
        let finalTime = get(this, 'educationTimeLength.value', null) + get(this, 'selectMonth.value', null)
        return fromDate.setMonth(fromDate.getMonth() + finalTime)
      }

      let finalTime = get(this, 'educationTimeLength.value', null) + get(this, 'selectMonth.value', null)
      
      if (this.educationTimeLength?.value && get(this, 'selectMonth.value', null)) {
        return fromDate.setMonth(fromDate.getMonth() + finalTime)
      } else {
        if(this.educationTimeLength.hasOwnProperty('value') && get(this, 'educationTimeLength.value', 0) > 0) {
          fromDate.setMonth(fromDate.getMonth() + finalTime)
        } else {
          toDate.setMonth(fromDate.getMonth() + finalTime)
        }
      }

      return toDate
    },

    calculatedEducationLength() {
      if(this.updatedLength && !get(this, 'selectMonth.value', null) ) {
        return get(this, 'educationTimeLength.value', 24) + ' månader'
      }
      return (get(this, 'educationTimeLength.value', 24) + get(this, 'selectMonth.value', null)) + ' månader'
    },

    isRegistryRole() {
      return this.currentUserRole === 'registry'
    },

    isAdminRole() {
      return this.currentUserRole === 'admin'
    },

    canExtend() {

      if ( this.currentUserRole === 'region' && !this.currentUser.regionIds.includes(this.book?.regionId) ) {
        return false
      }

      const extensions = get(this, 'extendEducationData', [])

      //- Check for any pending orders
      if ( extensions?.some(e => e.extensionOrders?.length) && extensions?.filter(extension => extension.extensionOrders?.some(order => order.stateId === 1)).length )
        return false

      if ( extensions.length )
        return extensions.every(o => o.approved)

      return true
    },

    isPendingOrder() {
      const extensions = get(this, 'extendEducationData', [])
      return extensions?.some(e => e.extensionOrders?.length) && extensions?.filter(extension => extension.extensionOrders?.some(order => order.stateId === 1)).length
    }
  },

  watch: {
    extendTime: {
      handler(val) {
        if(val) this.init()
        this.selectMonth = {}
      }
    }
  },

  methods: {
    get: get,

    init() {

      const educationId = get(this, 'book.educationId', -1)
      const id = get(this, 'book.education.educationDistanceEducationProviderCourses[0].distanceEducationProviderCourse.distanceEducationProviderId', -1)
      API.listDistanceEducationProviderExtensions(id).then(({ data }) => {
        API.getEducationExtensions(educationId).then((response) => {
          this.extendEducationData = response.data.rows
          this.updatedLength = this.extendEducationData.reduce((acc, it) => {
            acc += it.distanceEducationProviderExtension?.months || 0
            return acc;
          }, 0);
          this.initialStartDate = new Date().toISOString().substr(0, 10)
          const bookStartDate = get(this, 'book.education.startDate', null)
          const bookEndDate = get(this, 'book.education.calculatedEndDate', null)

          if(!this.book)
            return

          if(bookStartDate){
            this.initialStartDate = bookStartDate
          }

          if(bookStartDate && bookEndDate){
            let monthDiff = this.monthDiff(new Date(bookStartDate), new Date(bookEndDate))
            this.educationTimeLength = this.getQualificationLengthItems.find(m => m.value === monthDiff)
            if(! this.educationTimeLength) {
              this.educationTimeLength = 24
            }
          }

          this.agreementLength = 36

          try {
            const professionId = get(this, 'book.professionId', null)
            let profList = this.getProfessionById(professionId)?.qualificationTimeLength

            if ( typeof profList === 'string' ) {
              profList = JSON.parse(profList)
            }

            let baseLength = profList[professionId]?.length
            const tcs = get(this, 'selectedPerson.tradeCertificates', [])
            tcs.forEach(tc => {
              const tradeCertificateId = get(tc, 'profession.id', null)
              if(tradeCertificateId) {
                const tcLength = profList[tradeCertificateId].length
                  if (tcLength < baseLength) {
                    baseLength = tcLength
                  }
              }
            })
            this.agreementLength = baseLength
          } catch(e) {
            console.log(e)
          }

          this.timesLengths = data.rows.reduce((arr, item) => {

            let text = `${item.months} månader längre`

            arr.push({
              id: item.id,
              text: text,
              value: item.months,
              price: item.price
            })

            if(this.updatedLength) {
              return arr.filter(item => {
                const sum = this.maxExtendRange - get(this, 'updatedLength', null)
                if(sum % item.value === 0 && sum > 0) return item
              })
            }

            return arr
          }, []).reverse()
        })
      })
    },

    changeEducationTime() {
      if(! this.educationTimeLength && !(get(this, 'educationTimeLength.value', null)) || !(Object.values(this.selectMonth).some(i => i !== ''))){
        this.$emit('close')
        this.$events.fire('showError', 'Tyvärr gick det inte att spara.')
        return
      }

      let data = {
        issuerId: this.currentUserId,
        issuedAt: new Date(),
        educationId: get(this, 'book.education.id', -1),
        distanceEducationProviderId: get(this, 'book.education.educationDistanceEducationProviderCourses.0.distanceEducationProviderCourse.distanceEducationProviderId', -1),
        months: get(this, 'selectMonth.value', null),
        educationOldStartDate: get(this, 'book.education.startDate', null),
        educationOldEndDate: get(this, 'book.education.endDate', null)
      }

      API.extendEducation({data: data})
        .then(response => {
          this.$events.fire('showInfo', 'Skickad för godkännande.')
          this.$emit('close')
        })
        .catch(error => {
          console.log('Error: ', error)
          const errorMessage = error.response?.data || 'Tyvärr gick det inte att förlänga.'
          this.$events.fire('showError', errorMessage)
        })
    },

    monthDiff(d1, d2) {
      var months
      months = (d2.getFullYear() - d1.getFullYear()) * 12
      months -= d1.getMonth()
      months += d2.getMonth()
      return months <= 0 ? 0 : months
    }
  }
}
</script>
